import {
  ArrowSquareOut,
  CheckCircle,
  GlobeHemisphereEast,
  Info,
  LockOpen,
} from "@phosphor-icons/react";
import { formatCapeTownDate } from "@shared/lib/dates";
import type { CodingBenchmarkEvidence, CodingModelSignal } from "@shared/schemas/domain";
import { useMemo, useState } from "react";

type View = "independent" | "reasoning";

function independentResult(model: CodingModelSignal): CodingBenchmarkEvidence | null {
  return model.benchmarks.find(
    (result) => result.provenance === "independent" && result.benchmarkSlug === "swe-rebench",
  ) ?? null;
}

function sortByIndependentRank(models: CodingModelSignal[]): CodingModelSignal[] {
  return [...models].sort((left, right) =>
    (independentResult(left)?.rank ?? Number.POSITIVE_INFINITY)
    - (independentResult(right)?.rank ?? Number.POSITIVE_INFINITY));
}

function scoreLabel(result: CodingBenchmarkEvidence): string {
  return result.scoreUnit.includes("%") ? `${result.score}%` : `${result.score} ${result.scoreUnit}`;
}

export function ModelMap({ models }: { models: CodingModelSignal[] }) {
  const [view, setView] = useState<View>("independent");
  const [selectedId, setSelectedId] = useState<string | null>("gpt-5-6-sol-medium");
  const visibleModels = useMemo(() => {
    if (view === "independent") return sortByIndependentRank(models.filter((model) => independentResult(model)));
    return models
      .filter((model) => model.reasoningEfforts.length > 0)
      .sort((left, right) => {
        const providerOrder = Number(right.provider === "OpenAI") - Number(left.provider === "OpenAI");
        return providerOrder || left.model.localeCompare(right.model);
      });
  }, [models, view]);
  const selected = visibleModels.find((model) => model.id === selectedId) ?? visibleModels[0] ?? null;

  const chooseView = (next: View): void => {
    setView(next);
    setSelectedId("gpt-5-6-sol-medium");
  };

  return (
    <section className="model-section" aria-labelledby="models-heading">
      <div className="section-heading model-evidence-heading">
        <div>
          <p className="eyebrow">Coding model evidence</p>
          <h2 id="models-heading">Comparable where it counts.</h2>
          <p>One fresh independent window, separate provider claims, and the exact effort or agent configuration behind every score.</p>
        </div>
        <fieldset className="segmented-control model-view-control">
          <legend className="sr-only">Choose model evidence view</legend>
          <button type="button" aria-pressed={view === "independent"} onClick={() => chooseView("independent")}>Fresh independent</button>
          <button type="button" aria-pressed={view === "reasoning"} onClick={() => chooseView("reasoning")}>Reasoning guide</button>
        </fieldset>
      </div>

      <div className="model-evidence-shell">
        <fieldset className="model-evidence-list">
          <legend className="sr-only">{view === "independent" ? "Current SWE-rebench ranking" : "Models with explicit reasoning settings"}</legend>
          {visibleModels.map((model) => {
            const result = independentResult(model);
            const active = selected?.id === model.id;
            return (
              <button
                key={model.id}
                type="button"
                className="model-evidence-row"
                data-active={active ? "true" : "false"}
                aria-label={result
                  ? `${model.model}: ${scoreLabel(result)} on current SWE-rebench, ${result.configuration}`
                  : `${model.model}: provider-reported evidence only`}
                onClick={() => setSelectedId(model.id)}
              >
                <span className="model-evidence-row__rank">{result?.rank ? `#${result.rank}` : "New"}</span>
                <span className="model-evidence-row__identity">
                  <strong>{model.model}</strong>
                  <small>{model.provider} · {model.entityKind === "agent-system" ? "agent system" : model.openWeight ? "open weight" : "proprietary"}</small>
                </span>
                <span className="model-evidence-row__config">
                  <b>{view === "reasoning" ? model.recommendedEffort ?? "fixed" : result ? scoreLabel(result) : `${model.benchmarks.length} claims`}</b>
                  <small>{view === "reasoning" ? "recommended effort" : result?.configuration ?? "provider only"}</small>
                </span>
                <span className={`evidence-badge evidence-badge--${result ? "independent" : "provider"}`}>
                  {result ? "Independent" : "Provider"}
                </span>
              </button>
            );
          })}
        </fieldset>

        {selected && (
          <aside className="model-evidence-detail" aria-live="polite">
            <div className="model-evidence-detail__title">
              <span className="model-rank">{independentResult(selected)?.rank ? `#${independentResult(selected)?.rank}` : "Watch"}</span>
              <div>
                <strong>{selected.model}</strong>
                <span>{selected.provider} · {selected.region}</span>
              </div>
              {selected.openWeight ? <LockOpen aria-label="Open weight" /> : <GlobeHemisphereEast aria-hidden="true" />}
            </div>

            <p className="model-evidence-detail__summary">{selected.summary}</p>
            <dl className="model-evidence-detail__fit">
              <div><dt>Best fit</dt><dd>{selected.bestFor}</dd></div>
              <div><dt>Watch out</dt><dd>{selected.caveat}</dd></div>
            </dl>

            {selected.reasoningEfforts.length > 0 && (
              <div className="reasoning-strip">
                <div>
                  <span>Reasoning effort</span>
                  <strong>{selected.recommendedEffort ?? "Choose per task"}</strong>
                </div>
                <fieldset className="reasoning-strip__chips">
                  <legend className="sr-only">{selected.model} reasoning efforts</legend>
                  {selected.reasoningEfforts.map((effort) => <span key={effort} data-recommended={effort === selected.recommendedEffort ? "true" : "false"}>{effort}</span>)}
                </fieldset>
                {selected.reasoningMode && <p>{selected.reasoningMode}</p>}
              </div>
            )}

            <div className="benchmark-evidence">
              <h3>Evidence, not blended</h3>
              {selected.benchmarks.map((result) => (
                <article key={result.id} className="benchmark-evidence__row">
                  <div>
                    <span className={`evidence-badge evidence-badge--${result.provenance === "independent" ? "independent" : "provider"}`}>
                      {result.provenance === "independent" ? <CheckCircle aria-hidden="true" weight="fill" /> : null}
                      {result.provenance === "independent" ? "Independent" : "Provider-reported"}
                    </span>
                    <strong>{result.benchmarkName}</strong>
                    <small>{result.configuration}{result.harness ? ` · ${result.harness}` : ""}</small>
                  </div>
                  <b>{scoreLabel(result)}</b>
                  <p>{result.notes}</p>
                  <a className="external-link" href={result.sourceUrl} target="_blank" rel="noopener noreferrer">Source <ArrowSquareOut aria-hidden="true" /></a>
                </article>
              ))}
            </div>

            <div className="model-evidence-detail__footer">
              <span>{selected.releaseDate ? `Released ${formatCapeTownDate(selected.releaseDate)}` : `Verified ${formatCapeTownDate(selected.snapshotDate)}`}</span>
              {selected.contextLength && <span>{Math.round(selected.contextLength / 1_048_576 * 10) / 10}M context</span>}
              {selected.officialSourceUrl && <a className="external-link" href={selected.officialSourceUrl} target="_blank" rel="noopener noreferrer">Official details <ArrowSquareOut aria-hidden="true" /></a>}
            </div>
          </aside>
        )}
      </div>

      <p className="method-note"><Info aria-hidden="true" /> Independent ranks use the 15 May–1 July 2026 SWE-rebench window: 111 issues across 65 repositories. Provider-published scores remain visibly separate when the benchmark, effort, or harness differs.</p>
    </section>
  );
}
